import { useMsal } from "@azure/msal-react";
import { Callout, Icon, Link, Separator, Shimmer, Stack, Text } from "@fluentui/react";
import { useId } from '@fluentui/react-hooks';
import { AppLinkToExternal } from "global/Application";
import { Constants } from "global/Application/Constants";
import { AppDataSource } from "global/DataSources/AppDataSource";
import { MsalTokenSource } from "global/DataSources/MsalTokenSource";
import { bcbs_carrier, contact } from "model/CRM";
import { Ibcbs_carrierExtended } from "model/CRM.type";
import { IEntityReferenceValue } from "model/FlexibleAttributeModel";
import { useEffect, useRef, useState } from "react";

interface IProps {
    entity: IEntityReferenceValue;
    disabled?: boolean;
    styles?: any;
}

const EntityLink = (props: IProps) => {

    const [viewModel, setViewModel] = useState({ loading: true, data: null as contact | bcbs_carrier | null });
    const [isCalloutVisible, setIsCalloutVisible] = useState(false);
    const buttonId = useId('callout-button');

    const { instance } = useMsal();
    const tokenSource = new MsalTokenSource(instance);
    const dataSource = new AppDataSource(tokenSource);

    const countRef = useRef<NodeJS.Timeout | null>();


    useEffect(() => {
        (async () => {
            if (isCalloutVisible) {
                try {
                    setViewModel(t => { return { ...t, loading: true } });
                    switch (props.entity.entityType) {
                        case 'contact':
                            {
                                const data = await dataSource.getContact(props.entity.id);
                                setViewModel(t => {
                                    return {
                                        ...t,
                                        data: data,
                                    }
                                });
                            }
                            break;

                        case 'bcbs_carrier':
                            {

                                const data = await dataSource.getBcbsCarrier(props.entity.id);
                                setViewModel(t => {
                                    return {
                                        ...t,
                                        data: data,
                                    }
                                });
                            }
                            break;

                        case 'task':
                            {

                                const data = await dataSource.getTask(props.entity.id);
                                setViewModel(t => {
                                    return {
                                        ...t,
                                        data: data,
                                    }
                                });
                            }
                            break;

                        default:
                            break;
                    }


                } catch (error) {

                } finally {
                    setViewModel(t => { return { ...t, loading: false } });
                }
            } else if (!isCalloutVisible) {
                setViewModel(t => { return { ...t, data: null, } });
            }

        })();
    }, [isCalloutVisible]);

    function onRenderContactCallout() {

        const data = viewModel.data as contact;

        return <>
            {isCalloutVisible &&
                <Callout
                    gapSpace={0}
                    target={`#${buttonId}`}
                    onMouseLeave={() => { setIsCalloutVisible(false); }}
                    onMouseEnter={() => {
                        if (countRef.current) {
                            clearTimeout(countRef.current);
                            countRef.current = null;
                        }
                    }}
                    beakWidth={18}
                    styles={{ root: { borderRadius: '8px', padding: '12px', minWidth: 350, } }}
                >
                    <Stack>
                        <Stack tokens={{ childrenGap: 5 }}>
                            <Stack horizontal tokens={{ childrenGap: 5 }} verticalAlign={"center"}>
                                <Icon iconName="contact" styles={{ root: { margin: '8px', fontSize: '24px', paddingTop: '3px' } }} />
                                <Stack >
                                    <Text variant="small" styles={{ root: { fontWeight: '250' } }}>Name</Text>
                                    {viewModel.loading &&
                                        <Shimmer width="2060" />
                                    }
                                    {data &&
                                        <Stack horizontal>
                                            <Text variant="mediumPlus">{data?.fullname}</Text>
                                            <Icon
                                                iconName="Copy"
                                                styles={{ root: { fontSize: '12px', paddingLeft: '5px', cursor: 'pointer' } }}
                                                onClick={() => navigator.clipboard.writeText(data?.fullname ?? 'NA')}
                                            />
                                        </Stack>

                                    }
                                </Stack>

                            </Stack>
                            <Stack>
                                <Separator />
                                <Text variant="small" styles={{ root: { fontWeight: '250' } }}>Title</Text>
                                {viewModel.loading &&
                                    <Shimmer width="140" height='16' />
                                }
                                {data &&
                                    <Text>{data?.jobtitle ?? 'NA'}</Text>
                                }
                            </Stack>

                            <Stack>
                                <Text variant="small" styles={{ root: { fontWeight: '250' } }}>Email</Text>
                                {viewModel.loading &&
                                    <Shimmer width="50" />
                                }
                                {viewModel.data &&
                                    <Stack horizontal>
                                        <Link
                                            onClick={() => window.open(`mailto:${data?.emailaddress1}`)}
                                            disabled={!data?.emailaddress1}
                                        >
                                            <Text>{data?.emailaddress1 ?? 'NA'}</Text>
                                        </Link>
                                        <Icon
                                            iconName="Copy"
                                            styles={{ root: { fontSize: '12px', paddingLeft: '5px', cursor: 'pointer' } }}
                                            onClick={() => navigator.clipboard.writeText(data?.emailaddress1 ?? 'NA')}
                                        />
                                    </Stack>
                                }
                            </Stack>
                            <Stack>
                                <Text variant="small" styles={{ root: { fontWeight: '250' } }}>Business Phone</Text>
                                {viewModel.loading &&
                                    <Shimmer width="180" />
                                }
                                {viewModel.data &&
                                    <Stack horizontal>
                                        <Link
                                            onClick={() => window.open(`tel:${data?.telephone1}`)}
                                            disabled={!data?.telephone1}
                                        >
                                            <Text>{data?.telephone1 ?? 'NA'}</Text>
                                        </Link>
                                        <Icon
                                            iconName="Copy"
                                            styles={{ root: { fontSize: '12px', paddingLeft: '5px', cursor: 'pointer' } }}
                                            onClick={() => navigator.clipboard.writeText(data?.telephone1 ?? 'NA')}
                                        />
                                    </Stack>
                                }
                            </Stack>
                            <Stack>
                                <Text variant="small" styles={{ root: { fontWeight: '250' } }}>Fax</Text>
                                {viewModel.loading &&
                                    <Shimmer width="350" />
                                }
                                {viewModel.data &&
                                    <Stack horizontal>
                                        <Link
                                            onClick={() => window.open(`tel:${data?.fax}`)}
                                            disabled={!data?.fax}
                                        >
                                            <Text>{data?.fax ?? 'NA'}</Text>
                                        </Link>
                                        <Icon
                                            iconName="Copy"
                                            styles={{ root: { fontSize: '12px', paddingLeft: '5px', cursor: 'pointer' } }}
                                            onClick={() => navigator.clipboard.writeText(data?.fax ?? 'NA')}
                                        />
                                    </Stack>
                                }
                            </Stack>


                            {/* <Stack horizontal tokens={{ childrenGap: 15 }}>
                                <Icon iconName="Phone" onClick={() => />
                                <Icon iconName="OpenInNewTab" onClick={() => window.open(`${Constants.crm.baseUrl}/main.aspx?appid=${Constants.crm.appId}&forceUCI=1&pagetype=entityrecord&etn=${props.entity?.entityType}&id=${props.entity?.id}`)} />
                            </Stack> */}
                        </Stack>
                    </Stack>
                </Callout>
            }
        </>
    }


    function onRenderCarrierCallout() {
        const data = viewModel.data as Ibcbs_carrierExtended;

        const link_bcbs_carrier_parent = data?._bcbs_parentid_value
            ? AppLinkToExternal({ id: data._bcbs_parentid_value!, type: 'bcbs_carrier', })
            : undefined

        return <>
            {isCalloutVisible &&
                <Callout
                    gapSpace={0}
                    target={`#${buttonId}`}
                    onMouseLeave={() => { setIsCalloutVisible(false); }}
                    onMouseEnter={() => {
                        if (countRef.current) {
                            clearTimeout(countRef.current);
                            countRef.current = null;
                        }
                    }}
                    beakWidth={18}
                    styles={{ root: { borderRadius: '8px', padding: '12px', minWidth: 350, } }}
                >
                    <Stack>
                        <Stack tokens={{ childrenGap: 5 }}>
                            <Stack>
                                <Text variant="small" styles={{ root: { fontWeight: '250' } }}>Name</Text>
                                {viewModel.loading &&
                                    <Shimmer width="140" height='16' />
                                }
                                {viewModel.data &&
                                    <Stack horizontal>
                                        <Text>{data.bcbs_name ?? 'NA'}</Text>
                                        <Icon
                                            iconName="Copy"
                                            styles={{ root: { fontSize: '12px', paddingLeft: '5px', cursor: 'pointer' } }}
                                            onClick={() => navigator.clipboard.writeText(data?.bcbs_name ?? 'NA')}
                                        />
                                    </Stack>
                                }
                            </Stack>

                            <Stack>
                                <Separator />
                                <Text variant="small" styles={{ root: { fontWeight: '250' } }}>Parent</Text>
                                {viewModel.loading &&
                                    <Shimmer width="140" height='16' />
                                }
                                {viewModel.data &&

                                    <Link
                                        href={link_bcbs_carrier_parent}
                                        disabled={!data["_bcbs_parentid_value@OData.Community.Display.V1.FormattedValue"]}
                                        target='_blank'
                                    >
                                        <Text>{data["_bcbs_parentid_value@OData.Community.Display.V1.FormattedValue"] ?? 'NA'}</Text>
                                    </Link>
                                }
                            </Stack>

                            <Stack>
                                <Text variant="small" styles={{ root: { fontWeight: '250' } }}>Product</Text>
                                {viewModel.loading &&
                                    <Shimmer width="140" height='16' />
                                }
                                {viewModel.data &&
                                    <Text>{data.bcbs_keyproduct ?? 'NA'}</Text>
                                }
                            </Stack>

                            <Stack>
                                <Text variant="small" styles={{ root: { fontWeight: '250' } }}>Telephone Number</Text>
                                {viewModel.loading &&
                                    <Shimmer width="180" />
                                }
                                {viewModel.data &&
                                    <Stack horizontal>
                                        <Link
                                            onClick={() => window.open(`tel:${data?.bcbs_telephonenumber}`)}
                                            disabled={!data?.bcbs_telephonenumber}
                                        >
                                            <Text>{data.bcbs_telephonenumber ?? 'NA'}
                                            </Text>
                                        </Link>
                                        <Icon
                                            iconName="Copy"
                                            styles={{ root: { fontSize: '12px', paddingLeft: '5px', cursor: 'pointer' } }}
                                            onClick={() => navigator.clipboard.writeText(data?.bcbs_telephonenumber ?? 'NA')}
                                        />
                                    </Stack>
                                }
                            </Stack>
                            <Stack>
                                <Text variant="small" styles={{ root: { fontWeight: '250' } }}>Website</Text>
                                {viewModel.loading &&
                                    <Shimmer width="350" />
                                }
                                {viewModel.data &&
                                    <Stack horizontal>
                                        <Link
                                            onClick={() => window.open(data.bcbs_website)}
                                            disabled={!data.bcbs_website}
                                        >
                                            <Text>{data.bcbs_website ?? 'NA'}
                                            </Text>
                                        </Link>
                                        <Icon
                                            iconName="Copy"
                                            styles={{ root: { fontSize: '12px', paddingLeft: '5px', cursor: 'pointer' } }}
                                            onClick={() => navigator.clipboard.writeText(data?.bcbs_website ?? 'NA')}
                                        />
                                    </Stack>
                                }
                            </Stack>




                            {/* <Stack horizontal tokens={{ childrenGap: 15 }}>
                                <Icon iconName="Phone" onClick={() => />
                                <Icon iconName="OpenInNewTab" onClick={() => window.open(`${Constants.crm.baseUrl}/main.aspx?appid=${Constants.crm.appId}&forceUCI=1&pagetype=entityrecord&etn=${props.entity?.entityType}&id=${props.entity?.id}`)} />
                            </Stack> */}
                        </Stack>
                    </Stack>
                </Callout>
            }
        </>
    }

    function onRenderTaskCallout() {
        const data = viewModel.data as any;

        return <>
            {isCalloutVisible &&
                <Callout
                    gapSpace={0}
                    target={`#${buttonId}`}
                    onMouseLeave={() => { setIsCalloutVisible(false); }}
                    onMouseEnter={() => {
                        if (countRef.current) {
                            clearTimeout(countRef.current);
                            countRef.current = null;
                        }
                    }}
                    beakWidth={18}
                    styles={{ root: { borderRadius: '8px', padding: '12px', minWidth: 350, } }}
                >
                    <Stack>
                        <Stack tokens={{ childrenGap: 5 }}>
                            <Stack>
                                <Text variant="small" styles={{ root: { fontWeight: '250' } }}>Subject</Text>
                                {viewModel.loading &&
                                    <Shimmer width="140" height='16' />
                                }
                                {viewModel.data &&
                                    <Stack horizontal>
                                        <Text>{data.subject ?? 'NA'}</Text>
                                    </Stack>
                                }
                            </Stack>


                            <Stack>
                                <Text variant="small" styles={{ root: { fontWeight: '250' } }}>Description</Text>
                                {viewModel.loading &&
                                    <Shimmer width="140" height='120' />
                                }
                                {viewModel.data &&
                                    <Text>{data.description ?? 'NA'}</Text>
                                }
                            </Stack>



                            {/* <Stack horizontal tokens={{ childrenGap: 15 }}>
                                <Icon iconName="Phone" onClick={() => />
                                <Icon iconName="OpenInNewTab" onClick={() => window.open(`${Constants.crm.baseUrl}/main.aspx?appid=${Constants.crm.appId}&forceUCI=1&pagetype=entityrecord&etn=${props.entity?.entityType}&id=${props.entity?.id}`)} />
                            </Stack> */}
                        </Stack>
                    </Stack>
                </Callout>
            }
        </>
    }


    if (!props.entity.entityType)
        return <></>;

    let onRender: (() => JSX.Element) | null = null;

    switch (props.entity.entityType) {
        case 'contact':
            onRender = onRenderContactCallout
            break;

        case 'bcbs_carrier':
            onRender = onRenderCarrierCallout
            break;

        case 'task':
            onRender = onRenderTaskCallout
            break;
            
        default:
            break;
    }

    return <div>
        {onRender?.()}
        <Link
            id={buttonId}
            href={`${Constants.crm.baseUrl}/main.aspx?appid=${Constants.app.crmAppId}&forceUCI=1&pagetype=entityrecord&etn=${props.entity?.entityType}&id=${props.entity?.id}`}
            target="_blank"
            styles={props.styles}
            onClick={(event) => { event.stopPropagation() }}
            onMouseEnter={() => {
                if (countRef.current) {
                    clearTimeout(countRef.current);
                    countRef.current = null;
                }

                if (!isCalloutVisible)
                    countRef.current = setTimeout(() => {
                        setIsCalloutVisible(true);
                    }, 500);
            }}
            onMouseLeave={() => {
                if (countRef.current) {
                    clearTimeout(countRef.current);
                    countRef.current = null;
                }

                if (isCalloutVisible)
                    countRef.current = setTimeout(() => {
                        setIsCalloutVisible(false);
                        countRef.current = null;
                    }, 300);
            }}
        >
            {props.entity.name}
        </Link>
    </div >
}

export default EntityLink;

